import { Box, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
// TODO: move them to a different location
import { CheckCircle } from '../CheckCircle';
import { ErrorCircle } from '../ErrorCircle';
import { isString } from 'lib/typeInference';
import { BaseModal } from '../BaseModal';
import { ButtonV2 } from '../ButtonV2';
import { useTranslation } from 'react-i18next';
import { Image } from 'lib/Image';
import { useParams } from 'react-router';

const PREFIX = 'PayItemsAddedModal';

const classes = {
    root: `${PREFIX}root`,
    typography: `${PREFIX}typography`,
    img: `${PREFIX}img`,
    successDialogContent: `${PREFIX}successDialogContent`
};

const StyledBox = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    paddingTop: theme.spacing(1),
    [`&.${classes.root}`]: {
        zIndex: 99999
    },

    [`& .${classes.typography}`]: {
        fontWeight: 700
    },

    [`& .${classes.img}`]: {
        margin: theme.spacing(1, 0, 3, 0)
    },

    [`& .${classes.successDialogContent}`]: {
        marginBottom: theme.spacing(3)
    }
}));

type PropsWithLogo = {
    showLogo: true;
    variant?: never;
};

type PropsWithoutLogo = {
    showLogo?: false;
    variant?: 'success' | 'error';
};

interface BaseAlertProps extends React.PropsWithChildren {
    open: boolean;
    onCancel: () => void;
    title?: string;
    content?: React.ReactNode;
    buttonLabel?: string;
    disableBackdropClick?: boolean;
}

type IProps = BaseAlertProps & (PropsWithLogo | PropsWithoutLogo);

export const BaseAlert: React.FC<IProps> = ({
    open,
    onCancel,
    variant,
    title,
    content,
    buttonLabel,
    disableBackdropClick,
    showLogo
}) => {
    const [logoAvailable, setLogoAvailable] = React.useState(true);
    const theme = useTheme();
    const { t } = useTranslation();
    const { merchantId } = useParams();
    const Icon = React.useMemo(() => {
        switch (variant) {
            case 'success':
                return CheckCircle;
            case 'error':
                return ErrorCircle;
            default:
                return undefined;
        }
    }, [variant]);
    const handleLogoFallbackError = React.useCallback(() => {
        setLogoAvailable(false);
    }, []);
    return (
        <BaseModal
            open={open}
            className={classes.root}
            onClose={onCancel}
            disableBackdropClick={disableBackdropClick}
        >
            <StyledBox>
                {!!showLogo && logoAvailable && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        marginBottom={2}
                        marginTop={2}
                    >
                        <Image
                            src={`${process.env.MEDIA_URL}/tenants/${merchantId}/app_assets/pat_logo`}
                            onFallbackError={handleLogoFallbackError}
                            height="60px"
                        />
                    </Box>
                )}
                {!!Icon && <Icon className={classes.img} color={theme.palette.primary.main} />}
                {isString(title) && (
                    <Typography className={classes.typography} gutterBottom align="center" variant="h6">
                        {title}
                    </Typography>
                )}
                {isString(content) && (
                    <Typography className={classes.successDialogContent} gutterBottom align="center">
                        {content}
                    </Typography>
                )}
                <ButtonV2 fullWidth variant="contained" color="primary" onClick={onCancel}>
                    {buttonLabel ?? t('DIALOG_OK')}
                </ButtonV2>
            </StyledBox>
        </BaseModal>
    );
};
